import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Archive, Boxes, Layers, Pencil, Plus, RotateCcw, Ruler, Trash2 } from 'lucide-react';
import { formatQty, type Paginated } from '@erp/shared';
import { api, errorMessage } from '@/lib/api';
import { PageHeader, EmptyState, ErrorState, SearchInput } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/ui/table';
import { RowActions, type RowAction } from '@/components/ui/row-actions';
import { InlineView } from '@/components/ui/inline-view';
import { Field, Input, Select } from '@/components/ui/input';
import { useConfirm } from '@/components/ui/use-confirm';
import { toast } from 'sonner';

interface ModelRow {
  id: string; code: string; name: string; shape: string; width_mm: number; height_mm: number;
  thickness_mm: number; led_type: string | null; kelvin: number | null; power_w: number | null;
  active: boolean; packaging_mode: string; packaging_product_id: string | null;
  family_name: string | null; active_version_id: string | null; active_version: number | null;
  units_count: number; line_count: number; packaging_sku: string | null;
}

interface ProductOption { id: string; sku: string; name: string }

const EMPTY = {
  code: '', name: '', shape: 'rectangular', widthMm: '800', heightMm: '600', thicknessMm: '5',
  ledType: 'LED 4000K 24V', kelvin: '4000', powerW: '24', packagingProductId: '',
  packagingMode: 'fixed', isCustom: false, active: true
};

export function ModelsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  // Archivo: `activos` es lo normal; los archivados se miran aparte y se pueden reactivar.
  const [vista, setVista] = useState<'activos' | 'archivados'>('activos');
  const { confirm: confirmar, dialog: dialogoConfirmacion } = useConfirm();
  const { can, user } = useAuth();
  const esAdmin = Boolean(user?.roles.some((r) => r.code === 'admin'));
  const puedeEscribir = can('mirrors.write');

  const modelsQuery = useQuery({
    queryKey: ['models', vista],
    queryFn: () => api<{ data: ModelRow[] }>(`/mirror-models?active=${vista === 'activos' ? 'true' : 'false'}`)
  });
  const productsQuery = useQuery({ queryKey: ['packaging-options'], queryFn: () => api<Paginated<ProductOption>>('/products?type=packaging&pageSize=100&active=true') });

  const rows = (modelsQuery.data?.data ?? []).filter((row) =>
    !search || `${row.name} ${row.code}`.toLowerCase().includes(search.toLowerCase())
  );

  const columns: Column<ModelRow>[] = [
    {
      key: 'model',
      header: 'Modelo',
      cell: (row) => (
        <div className="min-w-[200px]">
          <p className="font-medium">{row.name}</p>
          <p className="font-mono text-[10px] text-muted-foreground">{row.code}</p>
        </div>
      )
    },
    {
      key: 'dims',
      header: 'Geometría',
      cell: (row) => <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Ruler className="size-3" /> {row.width_mm}×{row.height_mm}×{row.thickness_mm} mm · {row.shape}</span>
    },
    {
      key: 'led',
      header: 'Iluminación',
      cell: (row) => <div className="text-[11px]"><p>{row.led_type ?? '—'}</p><p className="text-muted-foreground">{row.kelvin ? `${row.kelvin}K` : ''} {row.power_w ? `· ${formatQty(row.power_w, 1)} W` : ''}</p></div>
    },
    {
      key: 'bom',
      header: 'BOM',
      cell: (row) => row.active_version ? <div><StatusBadge status="active" /><span className="ml-1 text-[10px] text-muted-foreground">v{row.active_version} · {row.line_count} líneas</span></div> : <Badge tone="warning">Sin BOM activa</Badge>
    },
    { key: 'packaging', header: 'Embalaje', cell: (row) => <div className="text-[11px]"><p className="font-mono">{row.packaging_sku ?? '—'}</p><p className="text-[10px] text-muted-foreground">{row.packaging_mode}</p></div> },
    { key: 'units', header: 'Unidades', cell: (row) => <span className="tabular-nums">{row.units_count}</span> },
    { key: 'status', header: 'Estado', cell: (row) => <StatusBadge status={row.active ? 'active' : 'inactive'} /> },
    {
      key: 'actions',
      header: '',
      cell: (row) => {
        // Mismo orden y mismo tamaño que en el resto de listados.
        const acciones: RowAction[] = [
          { key: 'edit', label: 'Editar', icon: <Pencil />, onClick: () => navigate(`/models/${row.id}`) }
        ];
        if (puedeEscribir) {
          acciones.push(row.active
            ? { key: 'archive', label: 'Archivar', icon: <Archive />, onClick: () => void archivar(row) }
            : { key: 'restore', label: 'Reactivar', icon: <RotateCcw />, onClick: () => void reactivar(row) });
        }
        if (esAdmin) {
          acciones.push({
            key: 'purge',
            label: 'Eliminar definitivamente',
            icon: <Trash2 />,
            destructive: true,
            disabled: row.units_count > 0,
            disabledReason: `No se puede borrar: tiene ${row.units_count} unidades fabricadas. Archívalo.`,
            onClick: () => void eliminar(row)
          });
        }
        // «Abrir» no hace falta como icono: la fila entera ya abre la ficha.
        return <RowActions actions={acciones} />;
      }
    }
  ];

  const refrescar = () => void qc.invalidateQueries({ queryKey: ['models'] });

  const archivar = async (row: ModelRow) => {
    const ok = await confirmar({
      title: `Archivar «${row.name}»`,
      description: 'Dejará de ofrecerse en los pedidos, pero se conserva el histórico. Se puede reactivar.',
      confirmLabel: 'Archivar'
    });
    if (!ok) return;
    try {
      await api(`/mirror-models/${row.id}`, { method: 'DELETE' });
      toast.success('Modelo archivado');
      refrescar();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const reactivar = async (row: ModelRow) => {
    try {
      await api(`/mirror-models/${row.id}`, { method: 'PATCH', body: { active: true } });
      toast.success('Modelo reactivado');
      refrescar();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const eliminar = async (row: ModelRow) => {
    const ok = await confirmar({
      title: `Eliminar «${row.name}» definitivamente`,
      description: 'No se puede deshacer. Si el modelo tiene unidades fabricadas o líneas de pedido, el sistema lo impedirá y habrá que archivarlo.',
      confirmLabel: 'Eliminar',
      destructive: true
    });
    if (!ok) return;
    try {
      await api(`/mirror-models/${row.id}?permanent=true`, { method: 'DELETE' });
      toast.success('Modelo eliminado');
      refrescar();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <div>
      <div className={formOpen ? 'hidden' : ''}>
      <PageHeader
        title="Modelos y BOM"
        description="Definición técnica del producto, composición versionada y embalaje asociado"
        actions={<Button onClick={() => setFormOpen(true)}><Plus /> Nuevo modelo</Button>}
      />
      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Buscar modelo…" className="w-full sm:w-72" />
          <Button size="sm" variant={vista === 'activos' ? 'default' : 'outline'}
            onClick={() => setVista('activos')}>Activos</Button>
          <Button size="sm" variant={vista === 'archivados' ? 'default' : 'outline'}
            onClick={() => setVista('archivados')}>Archivados</Button>
          <div className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground"><Layers className="size-3" /> {rows.length} modelos</div>
        </div>
        {modelsQuery.error ? <ErrorState message={(modelsQuery.error as Error).message} onRetry={() => void modelsQuery.refetch()} /> : (
          <DataTable
            columns={columns} rows={rows} loading={modelsQuery.isLoading} rowKey={(row) => row.id}
            onRowClick={(row) => navigate(`/models/${row.id}`)}
            empty={<EmptyState title="Sin modelos" description="Crea el primer modelo de espejo y define su BOM." icon={Boxes} />}
          />
        )}
      </Card>
      </div>

      {dialogoConfirmacion}

      <CreateModelModal
        open={formOpen}
        onOpenChange={setFormOpen}
        products={productsQuery.data?.data ?? []}
        onSaved={() => { setFormOpen(false); void qc.invalidateQueries({ queryKey: ['models'] }); }}
      />
    </div>
  );
}

function CreateModelModal({ open, onOpenChange, products, onSaved }: {
  open: boolean; onOpenChange: (open: boolean) => void; products: ProductOption[]; onSaved: () => void;
}) {
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const update = (key: keyof typeof form, value: string | boolean) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      if (!form.code.trim() || !form.name.trim()) throw new Error('Código y nombre son obligatorios');
      await api('/mirror-models', {
        method: 'POST',
        body: {
          code: form.code, name: form.name, shape: form.shape,
          widthMm: Number(form.widthMm), heightMm: Number(form.heightMm),
          thicknessMm: Number(form.thicknessMm), ledType: form.ledType || null,
          kelvin: Number(form.kelvin) || null, powerW: Number(form.powerW) || null,
          packagingProductId: form.packagingProductId || null, packagingMode: form.packagingMode,
          isCustom: form.isCustom, active: form.active
        }
      });
      toast.success('Modelo creado con BOM borrador');
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <InlineView
      open={open} onOpenChange={onOpenChange} size="lg" title="Nuevo modelo de espejo"
      description="Se crea automáticamente una BOM v1 en borrador."
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button loading={saving} onClick={() => void submit()}>Crear modelo</Button></>}
    >
      <div className="space-y-4">
        {error ? <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</div> : null}
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Código" required><Input value={form.code} onChange={(e) => update('code', e.target.value)} placeholder="LUNA-800" /></Field>
          <Field label="Nombre" required className="sm:col-span-2"><Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Luna 800×600" /></Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Forma"><Select value={form.shape} onChange={(e) => update('shape', e.target.value)}><option value="rectangular">Rectangular</option><option value="round">Redondo</option><option value="oval">Ovalado</option><option value="square">Cuadrado</option><option value="custom">A medida</option></Select></Field>
          <Field label="Ancho (mm)"><Input type="number" value={form.widthMm} onChange={(e) => update('widthMm', e.target.value)} /></Field>
          <Field label="Alto (mm)"><Input type="number" value={form.heightMm} onChange={(e) => update('heightMm', e.target.value)} /></Field>
          <Field label="Espesor (mm)"><Input type="number" value={form.thicknessMm} onChange={(e) => update('thicknessMm', e.target.value)} /></Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Tipo LED"><Input value={form.ledType} onChange={(e) => update('ledType', e.target.value)} /></Field>
          <Field label="Temperatura (K)"><Input type="number" value={form.kelvin} onChange={(e) => update('kelvin', e.target.value)} /></Field>
          <Field label="Potencia (W)"><Input type="number" step="0.1" value={form.powerW} onChange={(e) => update('powerW', e.target.value)} /></Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Embalaje asignado">
            <Select value={form.packagingProductId} onChange={(e) => update('packagingProductId', e.target.value)}>
              <option value="">Sin embalaje</option>
              {products.map((product) => <option key={product.id} value={product.id}>{product.sku} · {product.name}</option>)}
            </Select>
          </Field>
          <Field label="Modo de embalaje">
            <Select value={form.packagingMode} onChange={(e) => update('packagingMode', e.target.value)}>
              <option value="fixed">Referencia fija</option>
              <option value="parametric">Paramétrico por dimensiones</option>
            </Select>
          </Field>
        </div>
      </div>
    </InlineView>
  );
}
